/**
 * AusstellungenPage
 * Hub for the Ausstellungen & Veröffentlichungen section.
 * Lists the section intro plus a card per Kunstprojekt.
 */

import { Link } from 'react-router-dom'
import { useArtworks } from '@/hooks/useArtworks'
import { mediaUrl } from '@/utils/mediaUrl'
import { Markdown } from '@/components/ui/Markdown'
import { Seo } from '@/components/Seo'
import { useT, STRINGS, useFieldLang } from '@/context/LanguageContext'
import './ContentPage.css'

/**
 * Split the section intro into a leading welcome blurb and an optional
 * "Vita" timeline. The timeline starts at the first `## ` heading the
 * generator produced from a year prefix. If there is no heading, the
 * whole intro is treated as welcome.
 */
function splitIntro(intro: string): { welcome: string; timeline: string } {
  if (!intro) return { welcome: '', timeline: '' }
  const idx = intro.search(/^##\s/m)
  if (idx === -1) return { welcome: intro.trim(), timeline: '' }
  return {
    welcome: intro.slice(0, idx).trim(),
    timeline: intro.slice(idx).trim(),
  }
}

export function AusstellungenPage() {
  const { getSection, loading } = useArtworks()
  const section = getSection('ausstellungen')
  const t = useT()
  const pick = useFieldLang()

  if (loading) {
    return (
      <div className="loading-screen">
        <div className="loading-spinner" />
        <p>{t(STRINGS.common.loading)}</p>
      </div>
    )
  }

  if (!section) {
    return (
      <div className="error-screen">
        <p>{t(STRINGS.common.notFound)}</p>
      </div>
    )
  }

  const projects = section.projects || []
  const current = (section as any).current || []
  const introText = pick(section.intro, section.intro_en) || section.intro || ''
  const { welcome, timeline } = splitIntro(introText)

  return (
    <main className="content-page">
      <Seo
        title="Ausstellungen & Veröffentlichungen"
        description="Ausstellungen, Kunstprojekte und Veröffentlichungen von Anna Sacher Santana — vom Kunsttherapieprojekt Dazugehören bis zu den Erfurter Psychotherapiewochen."
      />
      <div className="content-container">
        <header className="content-header">
          <h1>{t(STRINGS.sections.ausstellungenHeader)}</h1>
          {welcome && <Markdown className="content-intro-prose">{welcome}</Markdown>}
        </header>

        {current.length > 0 && (
          <section className="ausstellungen-section ausstellungen-current">
            <h2 className="section-divider">{t(STRINGS.sections.currentExhibitions)}</h2>
            <div className="current-list">
              {current.map((ex: any) => {
                const cover = ex.images && ex.images[0]
                const coverUrl = cover ? mediaUrl(cover.url) : null
                const exTitle = pick(ex.title, ex.title_en) || ex.title
                const exDesc = pick(ex.description, ex.description_en) || ex.description
                const statusLabel =
                  ex.status === 'upcoming' ? t(STRINGS.sections.statusUpcoming) :
                  ex.status === 'past' ? t(STRINGS.sections.statusPast) :
                  t(STRINGS.sections.statusRunning)
                return (
                  <article key={ex.slug} className={`current-card current-${ex.status}`}>
                    {coverUrl && (
                      <div className="current-cover">
                        <img src={coverUrl} alt={cover?.alt || exTitle} loading="lazy" />
                      </div>
                    )}
                    <div className="current-body">
                      <span className={`current-status status-${ex.status}`}>{statusLabel}</span>
                      <h3 className="current-title">{exTitle}</h3>
                      <div className="current-meta">
                        {ex.datum && <span className="current-date">{ex.datum}</span>}
                        {ex.ort && <span className="current-ort">· {ex.ort}</span>}
                      </div>
                      {exDesc && <Markdown className="current-desc">{exDesc}</Markdown>}
                      {ex.url && (
                        <a className="current-link" href={ex.url} target="_blank" rel="noopener noreferrer">
                          {t(STRINGS.sections.moreInfo)}
                        </a>
                      )}
                    </div>
                  </article>
                )
              })}
            </div>
          </section>
        )}

        {projects.length > 0 && (
          <section className="ausstellungen-section">
            <h2 className="section-divider">{t(STRINGS.sections.artProjects)}</h2>
            <div className="content-card-grid">
              {projects.map((project) => {
                const cover = project.images[0]
                const coverUrl = cover ? mediaUrl(cover.url) : null
                const localTitle = pick(project.title, project.title_en) || project.title
                const localDesc = pick(project.description, project.description_en) || project.description
                const preview = localDesc
                  .replace(/<[^>]+>/g, ' ')
                  .replace(/^#+\s.*$/gm, '')
                  .replace(/[*_>`]/g, '')
                  .slice(0, 160)
                  .replace(/\s+/g, ' ')
                  .trim()
                const initials = localTitle
                  .split(/\s+/)
                  .filter(Boolean)
                  .slice(0, 2)
                  .map((w) => w[0])
                  .join('')
                  .toUpperCase()
                return (
                  <Link
                    key={project.slug}
                    to={`/ausstellungen/${project.slug}`}
                    className="content-card content-card-with-image"
                  >
                    <div className="content-card-image">
                      {coverUrl ? (
                        <img src={coverUrl} alt={cover?.alt || localTitle} loading="lazy" />
                      ) : (
                        <div className="content-card-placeholder" aria-hidden="true">
                          <span>{initials}</span>
                        </div>
                      )}
                    </div>
                    <div className="content-card-body">
                      <h3 className="content-card-title">{localTitle}</h3>
                      {preview && (
                        <p className="content-card-preview">
                          {preview}
                          {localDesc.length > 160 ? '…' : ''}
                        </p>
                      )}
                      <span className="content-card-link">
                        {project.images.length} {project.images.length === 1 ? t(STRINGS.common.image) : t(STRINGS.common.images)} →
                      </span>
                    </div>
                  </Link>
                )
              })}
            </div>
          </section>
        )}

        {timeline && (
          <section className="ausstellungen-section">
            <h2 className="section-divider">Ausstellungen & Veröffentlichungen</h2>
            <Markdown className="vita-timeline">{timeline}</Markdown>
          </section>
        )}
      </div>
    </main>
  )
}
